import type { Editor } from '@tiptap/core';
import type { EditorState } from '@tiptap/pm/state';
import { NodeSelection } from '@tiptap/pm/state';

export const getBlockStart = (state: EditorState, blockIndex: number) => {
	let pos = 0;
	for (let index = 0; index < blockIndex; index++) pos += state.doc.child(index).nodeSize;
	return pos;
};

export const selectImageAt = (editor: Editor, imagePos: number) => {
	if (editor.state.doc.nodeAt(imagePos)?.type.name !== 'image') return false;
	editor.view.dispatch(editor.state.tr.setSelection(NodeSelection.create(editor.state.doc, imagePos)));
	return true;
};

// updateAttributes drops the node selection, which closes the image menu mid-word.
export const setSelectedImageAlt = (editor: Editor, alt: string): boolean => {
	const { selection } = editor.state;
	if (!(selection instanceof NodeSelection) || selection.node.type.name !== 'image') return false;
	const transaction = editor.state.tr.setNodeMarkup(selection.from, undefined, { ...selection.node.attrs, alt });
	transaction.setSelection(NodeSelection.create(transaction.doc, selection.from));
	editor.view.dispatch(transaction);
	return true;
};

// Drops land only between top-level blocks so an image never splits a sentence.
export const moveImageToBlock = (editor: Editor, imagePos: number, targetIndex: number): boolean => {
	const { state } = editor;
	const image = state.doc.nodeAt(imagePos);
	if (image?.type.name !== 'image' || targetIndex < 0 || targetIndex > state.doc.childCount) return false;

	const blockIndex = state.doc.resolve(imagePos).index(0);
	const block = state.doc.child(blockIndex);
	const isAlone = block.type.name === 'paragraph' && block.childCount === 1;
	if (isAlone && (targetIndex === blockIndex || targetIndex === blockIndex + 1)) return false;

	const blockStart = getBlockStart(state, blockIndex);
	const imageEnd = imagePos + image.nodeSize;
	const hasTrailingSpace = state.doc.textBetween(imageEnd, Math.min(imageEnd + 1, state.doc.content.size)) === ' ';
	const deleteFrom = isAlone ? blockStart : imagePos;
	const deleteTo = isAlone ? blockStart + block.nodeSize : imageEnd + (hasTrailingSpace ? 1 : 0);
	const insertPos = getBlockStart(state, targetIndex);

	const transaction = state.tr.insert(insertPos, state.schema.nodes.paragraph.create(null, image));
	transaction.delete(transaction.mapping.map(deleteFrom), transaction.mapping.map(deleteTo));
	const movedImagePos = transaction.mapping.slice(1).map(insertPos + 1);
	transaction.setSelection(NodeSelection.create(transaction.doc, movedImagePos));
	editor.view.dispatch(transaction);
	return true;
};
